import { ReactElement, ReactNode } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  Icon,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { IoAdd, IoReorderThree, IoRemoveCircleOutline } from "react-icons/io5";
import {
  DndContext,
  pointerWithin,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { TextInput } from "../../atoms/AppInputs";
import { useModifiedTranslation } from "../../../hooks/useModifiedTranslation";
import { isInvalidPhrase, PhrasesSettingFormItem } from "./phraseSettingForm";

// ランダム枠のドロップ先ID。画面枠は画面番号(number)をIDにする
const RANDOM_SLOT_ID = "random";

function DraggablePhraseRow(
  props: Readonly<{
    id: string;
    index: number;
    register: UseFormRegister<PhrasesSettingFormItem>;
    isInvalid: boolean;
    onRemove: () => void;
  }>,
): ReactElement {
  const { id, index, register, isInvalid, onRemove } = props;
  const t = useModifiedTranslation();
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id });

  return (
    <Flex
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform) }}
      position="relative"
      zIndex={isDragging ? 1 : undefined}
      gap={2}
      py={2}
      align="center"
      bg="primary.bg_lv1"
    >
      <IconButton
        aria-label="Delete Phrase"
        variant="ghost"
        size="sm"
        icon={<Icon as={IoRemoveCircleOutline} boxSize={5} />}
        onClick={onRemove}
      />
      <TextInput
        isRequired={false}
        // 明示しないと囲みのFormControlのエラー状態が全ての文に付く
        isInvalid={isInvalid}
        size="sm"
        rounded="md"
        bg="common.base"
        placeholder={t("PhraseSetting.phrase")}
        data-testid={`phrase-setting-phrase-${index}`}
        {...register(`phrases.${index}.phrase`)}
      />
      {/* タッチ操作でスクロールせずにドラッグできるよう touchAction を切る */}
      <Center
        {...attributes}
        {...listeners}
        aria-label="Move Phrase"
        cursor="grab"
        sx={{ touchAction: "none" }}
        px={1}
      >
        <Icon as={IoReorderThree} boxSize={6} color="common_text.gray" />
      </Center>
    </Flex>
  );
}

function PhraseSlot(
  props: Readonly<{
    id: number | typeof RANDOM_SLOT_ID;
    title: string;
    emptyText?: string;
    addLabel: string;
    onAdd: () => void;
    children: ReactNode[];
  }>,
): ReactElement {
  const { id, title, emptyText, addLabel, onAdd, children } = props;
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <Box
      ref={setNodeRef}
      rounded="2xl"
      bg="primary.bg_lv1"
      borderWidth="2px"
      borderColor={isOver ? "primary.theme_lv1" : "transparent"}
      px={3}
      py={2}
    >
      <Flex align="baseline" gap={2}>
        <Text fontWeight="bold" fontSize="sm">
          {title}
        </Text>
        {children.length === 0 && emptyText && (
          <Text fontSize="xs" color="common_text.gray">
            {emptyText}
          </Text>
        )}
      </Flex>
      {children}
      <Button
        variant="ghost"
        color="primary.theme_lv1"
        w="full"
        size="sm"
        leftIcon={<IoAdd />}
        onClick={onAdd}
      >
        {addLabel}
      </Button>
    </Box>
  );
}

/*
 * 読み上げ文の入力。画面ごとの枠とランダム枠に振り分ける
 * 画面枠に複数入れるとその中から1つ、空の画面枠にはランダム枠から選ばれる
 */
export function PhraseListField(
  props: Readonly<{
    control: Control<PhrasesSettingFormItem>;
    register: UseFormRegister<PhrasesSettingFormItem>;
    setValue: UseFormSetValue<PhrasesSettingFormItem>;
    errorKey?: string;
  }>,
): ReactElement {
  const { control, register, setValue, errorKey } = props;
  const t = useModifiedTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "phrases",
  });
  const phrases = useWatch({ control, name: "phrases" });
  const numUtterance = useWatch({ control, name: "num_utterance" });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // 画面数を減らしても、はみ出した文が見えなくならないよう枠を残す
  // 読み上げ文の数より大きい画面数は保存できないので、枠は読み上げ文の数までにする
  const screenCount = Math.max(
    Math.min(Number(numUtterance) || 0, phrases.length),
    ...phrases.map((p) => p.disp_order ?? 0),
  );
  const screens = Array.from({ length: screenCount }, (_, i) => i + 1);

  const rowsOf = (dispOrder: number | null): ReactElement[] =>
    fields.flatMap((field, index) =>
      phrases[index]?.disp_order === dispOrder
        ? [
            <DraggablePhraseRow
              key={field.id}
              id={field.id}
              index={index}
              register={register}
              isInvalid={isInvalidPhrase(
                errorKey,
                phrases[index],
                Number(numUtterance),
              )}
              onRemove={() => remove(index)}
            />,
          ]
        : [],
    );

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event;
    if (!over) return;
    const index = fields.findIndex((f) => f.id === active.id);
    if (index === -1) return;
    setValue(
      `phrases.${index}.disp_order`,
      typeof over.id === "number" ? over.id : null,
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragEnd={handleDragEnd}
    >
      <VStack align="stretch" spacing={3}>
        {screens.map((n) => (
          <PhraseSlot
            key={n}
            id={n}
            title={`${t("PhraseSetting.screen")} ${n}`}
            emptyText={t("PhraseSetting.emptyScreen")}
            addLabel={t("PhraseSetting.addCandidate")}
            onAdd={() => append({ phrase: "", disp_order: n })}
          >
            {rowsOf(n)}
          </PhraseSlot>
        ))}
        <PhraseSlot
          id={RANDOM_SLOT_ID}
          title={t("PhraseSetting.random")}
          addLabel={t("PhraseSetting.addPhrase")}
          onAdd={() => append({ phrase: "", disp_order: null })}
        >
          {rowsOf(null)}
        </PhraseSlot>
      </VStack>
    </DndContext>
  );
}
